import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Fingerprint, LogIn, LogOut, UserCheck, Clock, Camera, CameraOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { AttendancePhoto } from "@/components/AttendancePhoto";

export default function Checador() {
  const { user } = useAuth();
  const pinOnly = user?.role !== "dueno" && !!user?.permissions?.checador?.pin_only;
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [pin, setPin] = useState("");
  const [lastEvent, setLastEvent] = useState(null);
  const [manualEmp, setManualEmp] = useState("");
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraStatus, setCameraStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 320, height: 240 } });
        if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setCameraStatus("ready");
      } catch {
        if (mounted) setCameraStatus("error");
      }
    })();
    return () => { mounted = false; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, []);

  const capturePhoto = () => {
    if (cameraStatus !== "ready" || !videoRef.current || !canvasRef.current) return null;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth || 320;
    canvas.height = video.videoHeight || 240;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.7);
  };

  const loadEmployees = async () => {
    const { data } = await api.get("/checador/employees");
    setEmployees(data);
  };

  const loadAttendance = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await api.get("/attendance", { params: { date_from: today, date_to: today } });
    setAttendance(data);
  };

  useEffect(() => { loadEmployees(); loadAttendance(); }, []);

  const submitPin = async (e) => {
    e?.preventDefault();
    if (!pin || pin.length < 4) return toast.error("Ingresa PIN de 4 dígitos");
    const photo_base64 = capturePhoto();
    if (!photo_base64) return toast.error("No se pudo tomar tu foto. Permite el acceso a la cámara para poder marcar tu entrada/salida.");
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/checkin-by-pin", { pin, photo_base64 });
      setLastEvent(data);
      toast.success(`${data.kind === "in" ? "Entrada" : "Salida"} registrada: ${data.employee_name}`);
      setPin("");
      loadAttendance();
    } catch (err) { toast.error(err.response?.data?.detail || "PIN inválido"); }
    finally { setBusy(false); }
  };

  const manualRegister = async (kind) => {
    if (!manualEmp) return toast.error("Elige empleado");
    setBusy(true);
    try {
      const { data } = await api.post("/attendance", { employee_id: manualEmp, kind, method: "manual" });
      setLastEvent(data);
      toast.success(`Registrado ${kind === "in" ? "entrada" : "salida"} de ${data.employee_name}`);
      setManualEmp("");
      loadAttendance();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const digits = (n) => (
    <button
      key={n}
      type="button"
      onClick={() => setPin((p) => (p.length < 6 ? p + n : p))}
      className="h-14 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 text-2xl font-outfit font-bold text-stone-800 dark:text-stone-100 hover:bg-[#1E3A8A]/5 dark:hover:bg-stone-900 transition-colors"
      data-testid={`pin-key-${n}`}
    >{n}</button>
  );

  return (
    <div className="max-w-screen-2xl mx-auto p-6 lg:p-8 space-y-6" data-testid="checador-page">
      <header>
        <h1 className="text-3xl font-outfit font-bold text-stone-900 dark:text-stone-100 flex items-center gap-2.5"><Fingerprint className="w-7 h-7 text-[#1E3A8A]" /> Checador</h1>
        <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">Registro de entradas y salidas del personal. No expone información de sueldos ni nómina.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">Empleados activos</div>
              <div className="mt-1 font-outfit font-semibold text-xl text-stone-900 dark:text-stone-100 tabular-nums">{employees.length}</div>
            </div>
            <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-[#1E3A8A]/10 text-[#1E3A8A]"><UserCheck className="w-4 h-4" strokeWidth={1.75} /></div>
          </div>
        </Card>
        <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">Eventos hoy</div>
              <div className="mt-1 font-outfit font-semibold text-xl text-stone-900 dark:text-stone-100 tabular-nums">{attendance.length}</div>
            </div>
            <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400"><Clock className="w-4 h-4" strokeWidth={1.75} /></div>
          </div>
        </Card>
        <Card className="p-4 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm" data-testid="late-today-card">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500 dark:text-stone-400">Retardos hoy</div>
              <div className="mt-1 font-outfit font-semibold text-xl text-stone-900 dark:text-stone-100 tabular-nums">{attendance.filter((a) => a.late).length}</div>
            </div>
            <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400"><AlertTriangle className="w-4 h-4" strokeWidth={1.75} /></div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PIN entry */}
        <Card className="p-6 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center"><Fingerprint className="w-5 h-5 text-[#1E3A8A]" /></div>
            <div>
              <h3 className="font-outfit font-bold text-lg">Registrar entrada / salida</h3>
              <p className="text-xs text-stone-500">Empleado marca con su PIN personal</p>
            </div>
          </div>
          <form onSubmit={submitPin} className="space-y-4">
            <div className="flex items-center gap-3 mb-1 rounded-lg border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900/40 p-2" data-testid="checador-camera-box">
              <div className="w-20 h-16 rounded-md overflow-hidden bg-stone-900 flex items-center justify-center flex-shrink-0 relative">
                <video ref={videoRef} autoPlay playsInline muted className={`w-full h-full object-cover ${cameraStatus === "ready" ? "" : "opacity-0"}`} />
                {cameraStatus !== "ready" && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    {cameraStatus === "loading" ? <Camera className="w-5 h-5 text-stone-500 animate-pulse" /> : <CameraOff className="w-5 h-5 text-red-500" />}
                  </div>
                )}
              </div>
              <div className="text-xs">
                {cameraStatus === "ready" && <span className="text-emerald-600 font-outfit font-semibold flex items-center gap-1" data-testid="camera-status-ready"><Camera className="w-3.5 h-3.5" /> Cámara lista — se toma tu foto al marcar</span>}
                {cameraStatus === "loading" && <span className="text-stone-500">Activando cámara…</span>}
                {cameraStatus === "error" && <span className="text-red-600 font-outfit font-semibold" data-testid="camera-status-error">Sin acceso a cámara: permite el permiso para poder marcar</span>}
              </div>
              <canvas ref={canvasRef} className="hidden" />
            </div>
            <div>
              <div className="flex items-center justify-center gap-2 mb-3 h-14 rounded-lg border-2 border-dashed border-stone-300 dark:border-stone-700 bg-stone-50/50 dark:bg-stone-900/30">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className={`w-6 h-8 rounded flex items-center justify-center text-lg font-outfit font-bold ${pin.length > i ? "bg-[#1E3A8A] text-white" : "bg-stone-200 dark:bg-stone-800 text-stone-400"}`}>
                    {pin.length > i ? "•" : ""}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map(digits)}
                <button type="button" onClick={() => setPin("")} className="h-14 rounded-lg border border-stone-200 bg-white text-sm font-outfit uppercase tracking-wider text-stone-500 hover:bg-red-50" data-testid="pin-clear-btn">Borrar</button>
                {digits("0")}
                <button type="submit" disabled={busy || pin.length < 4 || cameraStatus !== "ready"} className="h-14 rounded-lg bg-[#1E3A8A] text-white font-outfit font-bold uppercase tracking-wider text-sm hover:bg-[#1E40AF] disabled:opacity-40" data-testid="pin-submit-btn">Marcar</button>
              </div>
            </div>
            <p className="text-[10px] text-stone-500 text-center">Tu PIN alterna entre entrada y salida según tu último registro. Se toma una foto automática para evitar suplantaciones — no puedes marcar sin permitir la cámara.</p>
          </form>
        </Card>

        {/* Manual + last event */}
        <div className="space-y-4">
          {lastEvent && (
            <Card className={`p-6 border-2 shadow-sm ${lastEvent.kind === "in" ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800" : "bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-800"}`} data-testid="last-event-card">
              <div className="flex items-center gap-3">
                {lastEvent.kind === "in" ? <LogIn className="w-8 h-8 text-emerald-600" /> : <LogOut className="w-8 h-8 text-amber-600" />}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-outfit font-semibold text-stone-600">Último registro</div>
                  <div className="font-outfit font-bold text-xl text-stone-900">{lastEvent.employee_name}</div>
                  <div className="text-sm text-stone-700">{lastEvent.kind === "in" ? "Entrada" : "Salida"} · {lastEvent.time}</div>
                  {lastEvent.late && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-outfit font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded" data-testid="late-badge">
                      <AlertTriangle className="w-3 h-3" /> Retardo de {lastEvent.late_minutes} min
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {!pinOnly && (
          <Card className="p-5 border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-950 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <UserCheck className="w-4 h-4 text-[#1E3A8A]" />
              <h4 className="font-outfit font-semibold text-sm uppercase tracking-wider">Registro manual (supervisor)</h4>
            </div>
            <div className="space-y-2">
              <Select value={manualEmp} onValueChange={setManualEmp}>
                <SelectTrigger data-testid="manual-emp-select"><SelectValue placeholder="Elegir empleado…" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.position ? ` — ${e.position}` : ""}</SelectItem>)}</SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={() => manualRegister("in")} disabled={busy || !manualEmp} className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-checkin-btn"><LogIn className="w-4 h-4 mr-1" /> Entrada</Button>
                <Button onClick={() => manualRegister("out")} disabled={busy || !manualEmp} className="bg-amber-600 hover:bg-amber-700" data-testid="manual-checkout-btn"><LogOut className="w-4 h-4 mr-1" /> Salida</Button>
              </div>
            </div>
          </Card>
          )}
        </div>

        {/* Today's log full-width */}
        <div className="lg:col-span-2">
          <Card className="border-stone-200 dark:border-stone-800 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b bg-stone-50 dark:bg-stone-900 flex items-center justify-between">
              <h4 className="font-outfit font-semibold text-sm uppercase tracking-[0.1em] text-stone-700 dark:text-stone-300">Registros de hoy</h4>
              <span className="text-xs text-stone-500">{attendance.length} eventos</span>
            </div>
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 dark:bg-stone-900 sticky top-0">
                  <tr>{["Hora", "Foto", "Empleado", "Tipo", "Método", "Registró"].map((h) => <th key={h} className="px-4 py-2 text-left text-[10px] font-outfit font-semibold uppercase tracking-[0.1em] text-stone-500">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-stone-200 dark:divide-stone-800">
                  {attendance.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-stone-500">Aún no hay registros hoy.</td></tr> : attendance.map((a) => (
                    <tr key={a.id} data-testid={`att-row-${a.id}`}>
                      <td className="px-4 py-2 text-xs font-mono tabular-nums">{a.time}</td>
                      <td className="px-4 py-2"><AttendancePhoto path={a.photo_path} /></td>
                      <td className="px-4 py-2 text-sm font-medium">{a.employee_name}</td>
                      <td className="px-4 py-2">
                        {a.kind === "in" ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-700"><LogIn className="w-3 h-3" /> Entrada</span> : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700"><LogOut className="w-3 h-3" /> Salida</span>}
                        {a.late && (
                          <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-outfit font-semibold px-2 py-0.5 rounded bg-red-100 text-red-700" data-testid={`att-late-${a.id}`}>
                            <AlertTriangle className="w-3 h-3" /> +{a.late_minutes} min
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs uppercase tracking-wider text-stone-500">{a.method}</td>
                      <td className="px-4 py-2 text-xs text-stone-500">{a.recorded_by}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
